import {
  CarbonSegment,
  ProcessChain,
  LegendItem,
  getAllSegmentTypes,
  getMaxTotal,
  calculateTotal,
  sortSegments,
} from './carbonCalculation';

// Chart-specific constants
export const CUSTOM_COLOR_PALETTE = ['#264653', '#E9C46A', '#E76F51', '#2A9D8F', '#A06177'];

/**
 * Get consistent color for a segment type
 */
export function getSegmentColor(segmentLabel: string, allSegmentTypes: string[]): string {
  const segmentType = segmentLabel.toLowerCase();
  const index = allSegmentTypes.findIndex(type => segmentType.includes(type) || type.includes(segmentType));
  return CUSTOM_COLOR_PALETTE[index % CUSTOM_COLOR_PALETTE.length];
}

export interface ProcessedChain {
  name: string;
  total: number;
  chartData: any; // Chart.js data structure
  chartOptions: any; // Chart.js options
}

export interface ChartConfiguration {
  borderRadius: number;
  borderWidth: number;
  padding: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  useProportionalScaling: boolean;
}

// Default configurations for different chart types
export const SINGLE_CHAIN_CONFIG: ChartConfiguration = {
  borderRadius: 32,
  borderWidth: 2,
  padding: { left: 0, right: 0, top: 24, bottom: 0 },
  useProportionalScaling: false,
};

export const MULTIPLE_CHAIN_CONFIG: ChartConfiguration = {
  borderRadius: 16,
  borderWidth: 1,
  padding: { left: 0, right: 0, top: 0, bottom: 0 },
  useProportionalScaling: true,
};

/**
 * Calculate total value from segments
 */
export function calculateSegmentsTotal(segments: CarbonSegment[]): number {
  return segments.reduce((sum, segment) => sum + (segment.value || 0), 0);
}

/**
 * Generate legend items with consistent colors
 */
export function generateLegendItems(allSegmentTypes: string[]): LegendItem[] {
  return allSegmentTypes.map((segmentType, index) => ({
    label: segmentType,
    color: CUSTOM_COLOR_PALETTE[index % CUSTOM_COLOR_PALETTE.length],
  }));
}

/**
 * Generate Chart.js data structure
 */
export function generateChartData(
  segments: CarbonSegment[],
  config: ChartConfiguration,
  allSegmentTypes?: string[]
): any {
  const sortedSegments = sortSegments(segments);
  const useCustomColors = !allSegmentTypes; // Use custom colors for single chain

  return {
    labels: [''],
    datasets: sortedSegments.map((segment, i, arr) => {
      let borderRadius = { topLeft: 0, bottomLeft: 0, topRight: 0, bottomRight: 0 };

      // Single segment case - all corners rounded
      if (arr.length === 1) {
        borderRadius = {
          topLeft: config.borderRadius,
          bottomLeft: config.borderRadius,
          topRight: config.borderRadius,
          bottomRight: config.borderRadius,
        };
      }
      // First segment - left corners rounded
      else if (i === 0) {
        borderRadius = {
          topLeft: config.borderRadius,
          bottomLeft: config.borderRadius,
          topRight: 0,
          bottomRight: 0,
        };
      }
      // Last segment - right corners rounded
      else if (i === arr.length - 1) {
        borderRadius = {
          topLeft: 0,
          bottomLeft: 0,
          topRight: config.borderRadius,
          bottomRight: config.borderRadius,
        };
      }

      // Determine background color
      const backgroundColor = useCustomColors
        ? CUSTOM_COLOR_PALETTE[i % CUSTOM_COLOR_PALETTE.length]
        : getSegmentColor(segment.label, allSegmentTypes!);

      return {
        label: segment.label,
        data: [segment.value],
        backgroundColor,
        borderColor: '#ffffff',
        borderWidth: config.borderWidth,
        borderRadius,
        borderSkipped: false,
      };
    }),
  };
}

/**
 * Generate Chart.js options
 */
export function generateChartOptions(
  segments: CarbonSegment[],
  totalUnit: string,
  config: ChartConfiguration,
  maxTotal?: number,
  chainName?: string
): any {
  const total = calculateSegmentsTotal(segments);
  const sortedSegments = sortSegments(segments);
  const scaleMax = config.useProportionalScaling && maxTotal ? maxTotal : total;

  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        display: false,
        max: scaleMax,
      },
      y: {
        stacked: true,
        display: false,
      },
    },
    layout: {
      padding: config.padding,
    },
  };
}

/**
 * Generate chart data and options for a single chain (CarbonChartPart)
 */
export function generateSingleChainChart(segments: CarbonSegment[], totalUnit: string): any {
  return {
    data: generateChartData(segments, SINGLE_CHAIN_CONFIG),
    options: generateChartOptions(segments, totalUnit, SINGLE_CHAIN_CONFIG),
  };
}

/**
 * Generate Chart.js data structure specifically for CarbonChartPart (backward compatibility)
 */
export function generateCarbonChartPartData(segments: CarbonSegment[]): any {
  return generateChartData(segments, SINGLE_CHAIN_CONFIG);
}

/**
 * Generate Chart.js options specifically for CarbonChartPart (backward compatibility)
 */
export function generateCarbonChartPartOptions(segments: CarbonSegment[], totalUnit: string): any {
  return generateChartOptions(segments, totalUnit, SINGLE_CHAIN_CONFIG);
}

/**
 * Process multiple chains to include chart data and options (CarbonChartComparison)
 */
export function processMultipleChains(processChains: ProcessChain[], totalUnit: string): ProcessedChain[] {
  const allSegmentTypes = getAllSegmentTypes(processChains);
  const maxTotal = getMaxTotal(processChains);

  return processChains.map(chain => {
    const chainTotal = chain.total || calculateTotal(chain);

    return {
      name: chain.name,
      total: chainTotal,
      chartData: generateChartData(chain.segments, MULTIPLE_CHAIN_CONFIG, allSegmentTypes),
      chartOptions: generateChartOptions(chain.segments, totalUnit, MULTIPLE_CHAIN_CONFIG, maxTotal, chain.name),
    };
  });
}

/**
 * Process chains (backward compatibility alias)
 */
export function processChains(processChains: ProcessChain[], totalUnit: string): ProcessedChain[] {
  return processMultipleChains(processChains, totalUnit);
}

/**
 * Truncate label for display (used in chart legends)
 */
export function truncateLabel(label: string, maxWidth: number = 165): string {
  // Create a temporary canvas to measure text width
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return label; // Return original label if canvas context is not available
  }

  ctx.font = '14px Poppins';

  let labelText = label;
  let labelWidth = ctx.measureText(labelText).width;

  if (labelWidth > maxWidth) {
    // Crop the text and add ellipsis
    while (labelWidth > maxWidth - ctx.measureText('...').width && labelText.length > 0) {
      labelText = labelText.slice(0, -1);
      labelWidth = ctx.measureText(labelText).width;
    }
    labelText += '...';
  }

  return labelText;
}

/**
 * Generate legend items for multiple chains
 */
export function generateMultipleChainLegend(processChains: ProcessChain[]): LegendItem[] {
  const allSegmentTypes = getAllSegmentTypes(processChains);
  return generateLegendItems(allSegmentTypes);
}

/**
 * Generate legend items for single chain
 */
export function generateSingleChainLegend(segments: CarbonSegment[]): LegendItem[] {
  const sortedSegments = sortSegments(segments);
  return sortedSegments.map((segment, index) => ({
    label: segment.label,
    color: CUSTOM_COLOR_PALETTE[index % CUSTOM_COLOR_PALETTE.length],
    value: segment.value || 0, // Include the value with fallback to 0
  }));
}

/**
 * Helper function to draw legend items on canvas for single chain charts
 */
export function drawSingleChainLegend(
  ctx: CanvasRenderingContext2D,
  segments: CarbonSegment[],
  startX: number,
  startY: number,
  totalValue: string,
  totalUnit: string,
  labelColumnWidth: number = 200
): void {
  const valueColumnX = startX + labelColumnWidth;
  const formattedUnit = totalUnit.replace(/CO2|Co2|co2/gi, 'CO₂');
  const sortedSegments = sortSegments(segments);

  // Draw total value row first
  ctx.fillStyle = '#383e46';
  ctx.font = 'bold 14px Poppins';
  ctx.fillText('Total', startX, startY);

  // Right-align total value
  const totalValueText = `${totalValue} ${formattedUnit}`;
  const totalValueWidth = ctx.measureText(totalValueText).width;
  ctx.fillText(totalValueText, valueColumnX + 100 - totalValueWidth, startY);

  // Add separator line
  const separatorY = startY + 25;
  ctx.beginPath();
  ctx.strokeStyle = '#E5E7EB';
  ctx.moveTo(startX, separatorY - 10);
  ctx.lineTo(valueColumnX + 100, separatorY - 10);
  ctx.stroke();

  // Draw segment rows
  sortedSegments.forEach((segment, index) => {
    const y = separatorY + index * 25;

    // Draw color box
    ctx.fillStyle = CUSTOM_COLOR_PALETTE[index % CUSTOM_COLOR_PALETTE.length];
    ctx.fillRect(startX, y, 15, 15);

    // Use utility function for label truncation
    ctx.fillStyle = '#383e46';
    ctx.font = '14px Poppins';
    const maxLabelWidth = labelColumnWidth - 35;
    const truncatedLabel = truncateLabel(segment.label, maxLabelWidth);
    ctx.fillText(truncatedLabel, startX + 25, y + 12);

    // Right-align value
    ctx.fillStyle = '#838A93';
    const valueText = `${segment.value || 0} ${formattedUnit}`;
    const valueWidth = ctx.measureText(valueText).width;
    ctx.fillText(valueText, valueColumnX + 100 - valueWidth, y + 12);
  });
}
